import { classifyError, parseAssessment } from '@aimfora/ai'
import {
  createExam,
  deleteExam,
  getExamDetail,
  getMaterialText,
  listExams,
  saveGradeTable,
  saveRubric,
  updateExam,
} from '@aimfora/db/repositories'
import { notFound } from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import {
  createExamSchema,
  examIdSchema,
  parseAssessmentDocument,
  parsedRubricFromSuggestion,
  parseGradeTable,
  parseGradeTableTextSchema,
  saveGradeTableSchema,
  saveRubricSchema,
  updateExamSchema,
} from '~/lib/domain'
import { requireOrg } from '~/middleware/auth'
import { getAiRuntime } from '~/server/ai-runtime.server'
import { RequestRejected } from '~/server/rejected.server'
import { toExamDetailView, toExamListItemView } from '~/server/views.server'

/** Exams, their conversion table and their rubric. */

export const getExams = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .handler(async ({ context }) => (await listExams(context.scope)).map(toExamListItemView))

/** A missing exam, or one the caller may not see, is a plain "not found". */
export const getExam = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) => {
    const detail = await getExamDetail(context.scope, data.examId)
    if (!detail) throw notFound()
    return toExamDetailView(context.scope, detail)
  })

export const createExamFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(createExamSchema)
  .handler(async ({ context, data }) => {
    const row = await createExam(context.scope, data)
    return { id: row.id }
  })

export const updateExamFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(updateExamSchema)
  .handler(async ({ context, data }) => {
    const { examId, ...patch } = data
    await updateExam(context.scope, examId, patch)
  })

export const deleteExamFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) => {
    await deleteExam(context.scope, data.examId)
  })

export const saveGradeTableFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(saveGradeTableSchema)
  .handler(async ({ context, data }) => {
    await saveGradeTable(context.scope, data.examId, data.rows)
  })

export const saveRubricFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(saveRubricSchema)
  .handler(async ({ context, data }) => {
    await saveRubric(context.scope, data.examId, data.rubric)
  })

/** Read a conversion table from pasted text. Nothing is saved; the teacher checks it first. */
export const parseGradeTableTextFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(parseGradeTableTextSchema)
  .handler(({ data }) => ({ rows: parseGradeTable(data.text) }))

type ReadResult<T> =
  | { status: 'ok'; result: T }
  | { status: 'no_file' | 'processing' | 'unreadable' | 'nothing_found' }

/** Read the conversion table out of the uploaded file. Nothing is saved. */
export const readGradeTableFromMaterial = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) => {
    const found = await getMaterialText(context.scope, data.examId, 'grade_table')
    if (found.status !== 'ok') return { status: found.status } as ReadResult<never>
    const rows = parseGradeTable(found.text)
    return rows
      ? ({ status: 'ok', result: rows } as ReadResult<typeof rows>)
      : ({ status: 'nothing_found' } as ReadResult<never>)
  })

/** Read criteria and weights out of the uploaded assessment document. Nothing is saved. */
export const readRubricFromMaterial = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) => {
    const found = await getMaterialText(context.scope, data.examId, 'rubric')
    if (found.status !== 'ok') return { status: found.status } as ReadResult<never>
    const parsed = parseAssessmentDocument(found.text)
    return parsed.parts.length > 0
      ? ({ status: 'ok', result: parsed } as ReadResult<typeof parsed>)
      : ({ status: 'nothing_found' } as ReadResult<never>)
  })

/**
 * Read the rubric out of the assessment document with an AI model, for documents the plain reader
 * does not understand. The answer is only a suggestion, in the same shape as the plain reader's:
 * it fills the editor, and the teacher checks it before anything is saved. The document is the
 * teacher's own material, not student work.
 */
export const readRubricWithAi = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) => {
    const runtime = await getAiRuntime()
    if (!runtime) throw new RequestRejected('ai_not_configured')
    const found = await getMaterialText(context.scope, data.examId, 'rubric')
    if (found.status !== 'ok') return { status: found.status } as ReadResult<never>
    let suggestion: Awaited<ReturnType<typeof parseAssessment>>['output']
    try {
      suggestion = (
        await parseAssessment(
          runtime,
          { text: found.text },
          {
            observability: {
              distinctId: context.scope.ctx.userId,
              sessionId: `rubric-${data.examId}`,
              traceName: 'rubric_extraction',
            },
          },
        )
      ).output
    } catch (error) {
      // Only the code is kept: the error would carry the document
      const { code } = classifyError(error)
      throw new RequestRejected(code === 'provider_auth' ? 'ai_not_configured' : 'ai_failed')
    }
    const parsed = parsedRubricFromSuggestion(suggestion)
    return parsed.parts.length > 0
      ? ({ status: 'ok', result: parsed } as ReadResult<typeof parsed>)
      : ({ status: 'nothing_found' } as ReadResult<never>)
  })
